import React from 'react';
import PropTypes from 'prop-types';
import { useStaticQuery, graphql } from 'gatsby';
import { Heading, Box } from 'grommet';
import styled, { css } from 'styled-components';
import RichText from './RichText';
import { Button } from './Button';

export const StyledPageSection = styled.section`
  background-size: cover;
  background-position: center center;

  padding-top: 30px;
  padding-bottom: 30px;
  @media ${({ theme }) => theme.custom.layouts.above_large} {
    padding-top: 60px;
    padding-bottom: 60px;
  }

  background-image: ${({ backgroundImage }) =>
    backgroundImage && `url('${backgroundImage}')`};

  ${({ withDecoration, theme }) => {
    if (withDecoration) {
      return css`
        position: relative;
        &:after {
          content: '';
          display: block;
          width: 66%;
          position: absolute;
          bottom: -1.5rem;
          right: 0;
          height: 1.5rem;
          z-index: 1;
          background-color: ${theme.global.colors['accent-1']};
        }
      `;
    }
  }}

  ${({ backgroundColor }) => {
    switch (backgroundColor) {
      case 'light-grey':
        return css`
          background-color: ${({ theme }) => theme.global.colors['light-2']};
        `;
      case 'grey':
        return css`
          background-color: ${({ theme }) => theme.global.colors['light-3']};
        `;
      case 'dark':
        return css`
          background-color: ${({ theme }) => theme.global.colors['dark-4']};
        `;
      case 'black':
        return css`
          background-color: ${({ theme }) => theme.global.colors['black']};
        `;
      case 'white':
        return css`
          background-color: ${({ theme }) => theme.global.colors['light-1']};
        `;
      default:
        return css`
          background-color: ${({ theme }) => theme.global.colors['light-1']};
        `;
    }
  }}
`;

export const PageSection = React.forwardRef(
  (
    { backgroundColor, children, backgroundImage, withDecoration, ...rest },
    ref,
  ) => {
    return (
      <StyledPageSection
        ref={ref}
        withDecoration={withDecoration}
        backgroundImage={backgroundImage}
        backgroundColor={backgroundColor}
        {...rest}
      >
        {children}
      </StyledPageSection>
    );
  },
);

PageSection.defaultProps = {
  withDecoration: false,
  backgroundColor: 'white',
  backgroundImage: '',
};
PageSection.propTypes = {
  withDecoration: PropTypes.bool,
  backgroundImage: PropTypes.string,
  backgroundColor: PropTypes.oneOf(['white', 'grey', 'dark', 'black']),
  children: PropTypes.node.isRequired,
};

// SECTION HEADER

export const LeftVariety = css`
  width: 100%;
  text-align: left;
  @media ${({ theme }) => theme.custom.layouts.above_large} {
    > div {
      max-width: calc(8 / 12 * 100%);
    }
  }
`;

export const CenteredVariety = css`
  margin: 0 auto;
  text-align: center;
  width: 100%;

  @media ${({ theme }) => theme.custom.layouts.above_large} {
    max-width: calc(10 / 12 * 100%);
  }
`;

const StyledSectionHeaderTitle = styled.div`
  position: relative;
  max-width: 880px;
  margin: 0 auto;
  h1,
  h2,
  h3,
  h4,
  p {
    color: ${({ $theme }) => ($theme === 'light' ? 'inherit' : 'white')};
  }

  ${({ variety }) => {
    if (variety === 'left') {
      return css`
        @media ${({ theme }) => theme.custom.layouts.above_large} {
          &:before {
            content: '';
            position: absolute;
            top: 0;
            bottom: 0;
            display: block;
            height: 100%;
            background: ${({ theme }) => theme.global.colors['accent-2']};
            transition: all 0.3s ease;
            left: calc(var(--gutters) * -1);
          }
        }

        &:before {
          width: 5px;
        }

        @media ${({ theme }) => theme.custom.layouts.above_medium} {
          &:before {
            width: 5px;
          }
        }

        @media ${({ theme }) => theme.custom.layouts.above_large} {
          &:before {
            width: 10px;
          }
        }

        @media ${({ theme }) => theme.custom.layouts.above_xlarge} {
          &:before {
            width: 10px;
          }
        }
      `;
    }
    return css`
      text-align: center;
    `;
  }}
`;

// This is just the header and subtitle abstracted out for use outside of the section header
export const SectionHeaderTitle = ({
  title,
  subtitle,
  variety,
  theme,
  level,
}) => (
  <StyledSectionHeaderTitle variety={variety} $theme={theme}>
    <Heading level={level}>{title}</Heading>

    {subtitle !== null && (
      <Box margin={{ top: '20px' }}>
        <RichText richText={subtitle} />
      </Box>
    )}
  </StyledSectionHeaderTitle>
);

SectionHeaderTitle.defaultProps = {
  subtitle: null,
  theme: 'light',
  variety: 'left',
  level: 2,
};
SectionHeaderTitle.propTypes = {
  title: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  subtitle: PropTypes.object,
  variety: PropTypes.oneOf(['centered', 'left']),
  theme: PropTypes.oneOf(['dark', 'light']),
  level: PropTypes.number,
};

export const StyledSectionHeader = styled.div`
  ${({ theme }) => theme.custom.helpers.LayoutContainer``};
  padding: 40px 30px;
  @media ${({ theme }) => theme.custom.layouts.above_large} {
    padding-bottom: 60px;
  }

  > div {
    ${({ variety }) => {
      switch (variety) {
        case 'left':
          return LeftVariety;
        case 'centered':
        default:
          return CenteredVariety;
      }
    }}
  }
`;

export const SectionHeader = ({
  title,
  subtitle,
  variety,
  theme,
  headingLevel,
}) => (
  <StyledSectionHeader variety={variety}>
    <div>
      <SectionHeaderTitle
        theme={theme}
        title={title}
        subtitle={subtitle}
        variety={variety}
        level={headingLevel}
      />
    </div>
  </StyledSectionHeader>
);

SectionHeader.defaultProps = {
  subtitle: null,
  theme: 'light',
  headingLevel: 2,
  variety: 'centered',
};
SectionHeader.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.object,
  variety: PropTypes.oneOf(['centered', 'left']),
  theme: PropTypes.oneOf(['dark', 'light']),
  headingLevel: PropTypes.number,
};

export const SectionBody = styled.div`
  ${({ theme }) => theme.custom.helpers.LayoutContainer``};
`;

const StyledSectionFooter = styled.div`
  ${({ theme }) => theme.custom.helpers.LayoutContainer``};

  padding-top: 30px;
  @media ${({ theme }) => theme.custom.layouts.above_large} {
    padding-top: 60px;
  }
`;
export const SectionFooter = ({ text, ctaUrl, ctaLabel }) => (
  <StyledSectionFooter>
    <div>
      {text && (
        <Box>
          <Heading textAlign="center" level="5">
            {text}
          </Heading>
        </Box>
      )}
      {ctaUrl && ctaLabel && (
        <Box align="center" pad={{ top: text ? '30px' : '0px' }}>
          <Button label={ctaLabel} href={ctaUrl} />
        </Box>
      )}
    </div>
  </StyledSectionFooter>
);

SectionFooter.defaultProps = {
  text: null,
  ctaUrl: null,
  ctaLabel: null,
};
SectionFooter.propTypes = {
  text: PropTypes.string,
  ctaUrl: PropTypes.string,
  ctaLabel: PropTypes.string,
};
